import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../services/api';
import DetailTabs from '../../components/DetailTabs/DetailTabs';
import Seo, { SITE_ORIGIN } from '../../components/Seo';
import { getPlaceholderImage } from '../../utils/placeholder';
import { getPaymentMode } from '../../utils/productType';
import { loadHeroImageUrl } from '../../utils/heroImage';
import './WatchfaceDetailPage.css';
const WatchfaceDetailPage = () => {
    const { t, i18n } = useTranslation();
    const params = useParams();
    const idOrSlug = params.idOrSlug ?? params.slug;
    const [product, setProduct] = useState(null);
    const [colorThemes, setColorThemes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [heroLoaded, setHeroLoaded] = useState(false);
    const [heroImageUrl, setHeroImageUrl] = useState(null);
    const heroRef = useRef(null);
    useEffect(() => {
        const fetchData = async () => {
            if (!idOrSlug)
                return;
            try {
                setLoading(true);
                const productResponse = await apiService.getProductByIdOrWeblink(idOrSlug);
                if (productResponse.success) {
                    const prod = productResponse.data;
                    setProduct(prod);
                    const themesResponse = await apiService.getColorThemes(prod.id);
                    if (themesResponse.success) {
                        setColorThemes(themesResponse.data);
                    }
                }
                else {
                    setError(productResponse.error || t('watchfaceDetail.error.notFound', 'Watchface nicht gefunden'));
                }
            }
            catch (err) {
                setError(t('watchfaceDetail.error.loading', 'Fehler beim Laden des Watchfaces'));
            }
            finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [idOrSlug, t]);
    useEffect(() => {
        if (!product?.weblink) {
            setHeroLoaded(false);
            setHeroImageUrl(null);
            return;
        }
        let cancelled = false;
        setHeroLoaded(false);
        setHeroImageUrl(null);
        loadHeroImageUrl(product.weblink).then((url) => {
            if (!cancelled) {
                setHeroImageUrl(url);
                setHeroLoaded(!!url);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [product?.weblink]);
    if (loading) {
        return (_jsxs("div", { className: "loading-container", children: [_jsx(motion.div, { className: "loading-spinner", animate: { rotate: 360 }, transition: { duration: 1, repeat: Infinity, ease: "linear" } }), _jsx("p", { children: t('watchfaceDetail.loading', 'Watchface wird geladen...') })] }));
    }
    if (error || !product) {
        return (_jsxs("div", { className: "error-container", children: [_jsx("h2", { children: t('common.error', 'Fehler') }), _jsx("p", { children: error || t('watchfaceDetail.error.notFound', 'Watchface nicht gefunden') }), _jsx(Link, { to: "/", className: "back-link", children: t('watchfaceDetail.backToOverview', 'Zurück zur Übersicht') })] }));
    }
    const canonicalSlug = (product.weblink || '').replace(/\/$/, '') || String(product.id);
    const absoluteImage = product.coverImage
        ? (product.coverImage.startsWith('http') ? product.coverImage : `${SITE_ORIGIN}${product.coverImage}`)
        : undefined;
    const pageUrl = `${SITE_ORIGIN}/watchface/${canonicalSlug}`;
    const productJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        brand: { '@type': 'Brand', name: 'TitanicTurtle' },
        ...(absoluteImage ? { image: absoluteImage } : {}),
        url: pageUrl,
        ...(product.price
            ? {
                offers: {
                    '@type': 'Offer',
                    price: product.price.toFixed(2),
                    priceCurrency: 'EUR',
                    availability: 'https://schema.org/InStock',
                    url: pageUrl,
                },
            }
            : {}),
    };
    const breadcrumbJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Watchfaces', item: `${SITE_ORIGIN}/` },
            { '@type': 'ListItem', position: 2, name: product.name, item: pageUrl },
        ],
    };
    return (_jsxs("div", { className: "watchface-detail-page", children: [_jsx(Seo, { title: `${product.name} – Garmin Connect IQ`, description: `${product.name} – TitanicTurtle for Garmin`, canonicalPath: `/watchface/${canonicalSlug}`, type: "product", image: absoluteImage, jsonLd: [productJsonLd, breadcrumbJsonLd] }), _jsx("div", { className: "detail-top-bar", children: _jsxs(Link, { to: "/", className: "detail-back-button", children: ["\u2190 ", t('watchfaceDetail.backToOverview', 'Zurück zur Übersicht')] }) }), _jsxs("div", { className: "detail-container", children: [_jsxs(motion.div, { className: `detail-hero ${heroLoaded ? 'detail-hero--has-bg' : ''}`, ref: heroRef, initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 }, children: [heroLoaded && heroImageUrl && (_jsx("div", { className: "detail-hero-bg", style: { backgroundImage: `url(${heroImageUrl})` } })), _jsx("div", { className: "hero-image-col", children: _jsx("a", { href: product.storelink, target: "_blank", rel: "noopener noreferrer", className: "image-wrapper-link", children: _jsx("div", { className: "image-wrapper", children: _jsx("img", { src: product.coverImage, alt: product.name, className: "product-cover-image", width: 300, height: 400, onError: (e) => {
                                                e.target.src = getPlaceholderImage(product.name);
                                            } }) }) }) }), _jsxs("div", { className: "hero-info-col", children: [_jsx("h1", { className: "hero-title", children: _jsx("span", { className: "title-gradient", children: product.name }) }), _jsxs("div", { className: "hero-primary-row", children: [_jsxs("div", { className: "hero-store-links", children: [product.storelink && (_jsx("a", { href: product.storelink, target: "_blank", rel: "noopener noreferrer", className: "store-link-btn store-btn-primary", children: t('watchfaceDetail.goToStore', 'Go to IQ Store') })), product.triallink && (_jsx("a", { href: product.triallink, target: "_blank", rel: "noopener noreferrer", className: "store-link-btn store-btn-secondary", children: t('watchfaceDetail.goToTrial', 'Try for free (VX)') }))] }), getPaymentMode(product.is_vx) === 1 && (product.vxVersion || product.eliteVersion) ? (_jsxs("div", { className: "hero-meta", children: [product.vxVersion && (_jsxs("span", { className: "meta-item", children: ["VX: v", product.vxVersion] })), product.eliteVersion && (_jsxs("span", { className: "meta-item", children: ["Elite: v", product.eliteVersion] }))] })) : product.latestExternalVersion ? (_jsx("div", { className: "hero-meta", children: _jsxs("span", { className: "meta-item", children: ["v", product.latestExternalVersion] }) })) : null] }), i18n.language.startsWith('ru') && (_jsx("div", { className: "russia-payment-warning", children: t('watchfaceDetail.russiaPaymentWarning', 'В данный момент из-за различных санкций некоторые или все методы оплаты могут не работать. Как разработчик я, к сожалению, не могу на это повлиять и не могу предложить собственные решения.') }))] })] }), _jsx(motion.section, { className: "detail-tabs-section", initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.2, duration: 0.5 }, children: _jsx(DetailTabs, { product: product, colorThemes: colorThemes }) })] })] }));
};
export default WatchfaceDetailPage;
